<template>
  <div
    ref="TimeDurationRef"
    class="time-input full-width"
    :class="{
      'time-input--readonly': props.readonly,
      'time-input--compact': props.compact,
    }"
  >
    <span
      v-if="props.label"
      class="time-input__label"
      :class="{
        'time-input__label--error': inputError,
      }"
    >
      {{ props.label }}
    </span>
    <div :class="{ 'time-input': inputError }">
      <div
        class="time-input__container"
        :class="{
          'time-input--error': inputError || props.required,
          'time-input__container--border': !props.stylesNoBorder,
          'time-input__container--no-border': props.stylesNoBorder,
        }"
      >
        <octicon
          :class="{
            'tw-mx-2 tw-size-4': !props.compact,
            'tw-ml-0 tw-mr-2': props.stylesNoBorder,
            'tw-size-3.5': props.compact,
          }"
          icon="stopwatch"
        />

        <!-- Ledger mode input (right-fill numeric) -->
        <input
          v-if="inputMode === 'ledger'"
          ref="inputRef"
          :value="ledgerDisplayValue"
          type="text"
          inputmode="numeric"
          class="time-input__field"
          :placeholder="'00:00'"
          :readonly="props.readonly"
          @keydown="handleLedgerKeyDown"
          @focus="handleLedgerFocus"
          @blur="handleLedgerBlur"
          @select="handleLedgerSelect"
          @mouseup="handleLedgerMouseUp"
          @paste.prevent
          @drop.prevent
        />

        <!-- Natural mode input (flexible text) -->
        <input
          v-else
          ref="inputRef"
          v-model="naturalInputValue"
          type="text"
          class="time-input__field"
          :placeholder="'e.g. 1h 30m'"
          :readonly="props.readonly"
          @focus="handleNaturalFocus"
          @blur="handleNaturalBlur"
          @keydown="handleNaturalKeyDown"
        />

        <!-- Mode toggle buttons -->
        <div
          v-if="!props.readonly && !props.hideToggle"
          class="time-input__mode-toggle"
        >
          <button
            type="button"
            class="time-input__mode-btn"
            :class="{ 'time-input__mode-btn--active': inputMode === 'natural' }"
            @click="setInputMode('natural')"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 16 16"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M2.5 4.5A1.5 1.5 0 0 1 4 3h8a1.5 1.5 0 0 1 1.5 1.5v.5H14a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1h-.5v.5A1.5 1.5 0 0 1 12 9H4a1.5 1.5 0 0 1-1.5-1.5V7H2a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h.5v.5zM4 4a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h8a.5.5 0 0 0 .5-.5v-3A.5.5 0 0 0 12 4H4zm0 9a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-1z"
              />
            </svg>
            <q-tooltip :delay="250">
              Natural input mode - type flexibly (e.g. 1h 30m, 90 minutes)
            </q-tooltip>
          </button>
          <button
            type="button"
            class="time-input__mode-btn"
            :class="{ 'time-input__mode-btn--active': inputMode === 'ledger' }"
            @click="setInputMode('ledger')"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 16 16"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2zm2-1a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H2z"
              />
              <path
                d="M4 4h2v2H4V4zm4 0h2v2H8V4zm4 0h2v2h-2V4zM4 7h2v2H4V7zm4 0h2v2H8V7zm4 0h2v2h-2V7zM4 10h2v2H4v-2zm4 0h2v2H8v-2zm4 0h2v2h-2v-2z"
              />
            </svg>
            <q-tooltip :delay="250">
              Ledger mode - fast numeric entry (digits fill right-to-left)
            </q-tooltip>
          </button>
        </div>
      </div>
      <span
        v-if="inputErrorMessage && inputError"
        class="time-input__error-message"
      >
        {{ inputErrorMessage }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useStorage } from '@vueuse/core';
import parse from 'parse-duration';
import { LocalStorageKeys } from '@/utils/local-storage';

type InputMode = 'natural' | 'ledger';

interface Props {
  label?: string;
  seconds?: number;
  stylesNoBorder?: boolean;
  readonly?: boolean;
  compact?: boolean;
  required?: boolean;
  autoFocus?: boolean;
  hideToggle?: boolean;
}

interface Emits {
  (e: 'seconds-updated', value: number): void;
  (e: 'time-duration-clicked'): void;
  (e: 'blur'): void;
}

const emit = defineEmits<Emits>();

const props = withDefaults(defineProps<Props>(), {
  stylesNoBorder: false,
  readonly: false,
  required: false,
  autoFocus: false,
  hideToggle: false,
});

const TimeDurationRef = ref<HTMLInputElement | null>(null);
const inputRef = ref<HTMLInputElement | null>(null);
const inputError = ref(false);
const inputErrorMessage = ref('');

// Persisted input mode preference
const inputMode = useStorage<InputMode>(
  LocalStorageKeys.TIME_DURATION_INPUT_MODE,
  'natural'
);

// ============== SHARED STATE ==============

// Internal seconds value (source of truth)
const internalSeconds = ref(0);

// Format seconds as human-readable display (e.g., "1h 30m")
function formatSecondsForDisplay(seconds: number): string {
  if (seconds === 0) return '';
  const totalMins = Math.round(seconds / 60);
  const hours = Math.floor(totalMins / 60);
  const minutes = totalMins % 60;
  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
}

// Convert seconds to raw digits (needed for initialization)
function secondsToRawDigits(seconds: number): number {
  const totalMins = Math.round(seconds / 60);
  const hours = Math.floor(totalMins / 60);
  const minutes = totalMins % 60;
  return hours * 100 + minutes;
}

// ============== LEDGER MODE (Right-fill numeric) ==============

// Store raw digits as HHMM number (e.g., 130 = 01:30, 12345 = 123:45)
const rawDigits = ref(secondsToRawDigits(props.seconds || 0));

// Track if we just focused - first digit will reset
const freshFocus = ref(false);

// Format raw digits as HHH:MM display
const ledgerDisplayValue = computed(() => {
  const minutes = rawDigits.value % 100;
  const hours = Math.floor(rawDigits.value / 100);
  const paddedHours = hours.toString().padStart(2, '0');
  const paddedMinutes = minutes.toString().padStart(2, '0');
  return `${paddedHours}:${paddedMinutes}`;
});

// Check if current raw digits represent valid time (minutes <= 59)
const isLedgerValid = computed(() => {
  const minutes = rawDigits.value % 100;
  return minutes <= 59;
});

// Convert raw digits to seconds
function rawDigitsToSeconds(): number {
  const minutes = rawDigits.value % 100;
  const hours = Math.floor(rawDigits.value / 100);
  return (hours * 60 + minutes) * 60;
}

function handleLedgerKeyDown(event: KeyboardEvent) {
  if (props.readonly) return;

  const key = event.key;

  // Handle digit input (0-9)
  if (/^[0-9]$/.test(key)) {
    event.preventDefault();

    const digit = parseInt(key, 10);

    // If first digit after focus, start fresh
    if (freshFocus.value) {
      freshFocus.value = false;
      rawDigits.value = digit;
    } else {
      // Shift left and append digit (cap at 8 digits: 99999999 = 999999:99)
      const newValue = rawDigits.value * 10 + digit;
      rawDigits.value = newValue > 99999999 ? newValue % 100000000 : newValue;
    }

    // Clear any previous error while typing
    inputError.value = false;
    inputErrorMessage.value = '';

    nextTick(() => selectAll());
    return;
  }

  // Handle Backspace/Delete - clear to 00:00
  if (key === 'Backspace' || key === 'Delete') {
    event.preventDefault();
    freshFocus.value = false;
    rawDigits.value = 0;
    inputError.value = false;
    inputErrorMessage.value = '';
    nextTick(() => selectAll());
    return;
  }

  // Handle Enter
  if (key === 'Enter') {
    // Validate before accepting
    if (!validateLedgerAndEmit()) {
      event.preventDefault();
      return;
    }

    // For Ctrl/Cmd+Enter: let bubble for modal save
    if (event.ctrlKey || event.metaKey) {
      return;
    }

    // Plain Enter - blur
    event.preventDefault();
    inputRef.value?.blur();
    return;
  }

  // Handle Escape - revert to original and blur
  if (key === 'Escape') {
    event.preventDefault();
    event.stopPropagation();
    // Revert to prop value
    rawDigits.value = secondsToRawDigits(props.seconds || 0);
    inputError.value = false;
    inputErrorMessage.value = '';
    inputRef.value?.blur();
    return;
  }

  // Handle Tab - validate first, allow natural behavior
  if (key === 'Tab') {
    if (!validateLedgerAndEmit()) {
      event.preventDefault();
    }
    return;
  }

  // Prevent all other keys (arrows, letters, etc.)
  if (!event.ctrlKey && !event.metaKey) {
    event.preventDefault();
  }
}

function handleLedgerFocus() {
  if (props.readonly) return;

  emit('time-duration-clicked');

  // Mark as fresh focus - first digit will start from 0
  freshFocus.value = true;

  // Always select all on focus
  nextTick(() => selectAll());
}

function handleLedgerBlur() {
  freshFocus.value = false;
  validateLedgerAndEmit();
  emit('blur');
}

function handleLedgerSelect(event: Event) {
  // Force select all - don't allow partial selection
  const input = event.target as HTMLInputElement;
  if (input.selectionStart !== 0 || input.selectionEnd !== input.value.length) {
    nextTick(() => selectAll());
  }
}

function handleLedgerMouseUp() {
  // Ensure full selection after mouse interaction
  nextTick(() => selectAll());
}

function selectAll() {
  if (inputRef.value) {
    inputRef.value.setSelectionRange(0, inputRef.value.value.length);
  }
}

function validateLedgerAndEmit(): boolean {
  if (!isLedgerValid.value) {
    inputError.value = true;
    inputErrorMessage.value = 'Minutes cannot exceed 59';
    return false;
  }

  inputError.value = false;
  inputErrorMessage.value = '';
  const seconds = rawDigitsToSeconds();
  internalSeconds.value = seconds;
  emit('seconds-updated', seconds);
  return true;
}

// ============== NATURAL MODE (Flexible text input) ==============

const naturalInputValue = ref(formatSecondsForDisplay(props.seconds || 0));

// Initialize from seconds prop (watch must be after rawDigits and naturalInputValue are declared)
watch(
  () => props.seconds,
  (seconds) => {
    internalSeconds.value = seconds || 0;
    // Update mode-specific state
    rawDigits.value = secondsToRawDigits(internalSeconds.value);
    naturalInputValue.value = formatSecondsForDisplay(internalSeconds.value);
  }
);

function handleNaturalFocus() {
  if (props.readonly) return;
  emit('time-duration-clicked');
}

function handleNaturalBlur() {
  validateNaturalAndEmit();
  emit('blur');
}

function handleNaturalKeyDown(event: KeyboardEvent) {
  if (props.readonly) return;

  const key = event.key;

  // Handle Enter
  if (key === 'Enter') {
    // Validate before accepting
    if (!validateNaturalAndEmit()) {
      event.preventDefault();
      return;
    }

    // For Ctrl/Cmd+Enter: let bubble for modal save
    if (event.ctrlKey || event.metaKey) {
      return;
    }

    // Plain Enter - blur
    event.preventDefault();
    inputRef.value?.blur();
    return;
  }

  // Handle Escape - revert to original and blur
  if (key === 'Escape') {
    event.preventDefault();
    event.stopPropagation();
    // Revert to prop value
    naturalInputValue.value = formatSecondsForDisplay(props.seconds || 0);
    inputError.value = false;
    inputErrorMessage.value = '';
    inputRef.value?.blur();
    return;
  }

  // Handle Tab - validate first, allow natural behavior
  if (key === 'Tab') {
    if (!validateNaturalAndEmit()) {
      event.preventDefault();
    }
    return;
  }
}

function parseNaturalInput(input: string): number | null {
  const trimmed = input.trim();

  // Empty input = 0
  if (!trimmed) return 0;

  // 1. Try HH:MM format first (e.g., "2:45" -> 2h 45m)
  //    Must check before parse-duration as it mishandles colons
  const colonMatch = trimmed.match(/^(\d+):(\d{1,2})$/);
  if (colonMatch) {
    const hours = parseInt(colonMatch[1], 10);
    const minutes = parseInt(colonMatch[2], 10);
    if (minutes <= 59) {
      return (hours * 60 + minutes) * 60;
    }
  }

  // 2. Try plain number (interpreted as hours, e.g., "1" -> 1h, "1.5" -> 1h 30m)
  //    Must check before parse-duration as it interprets bare numbers as seconds
  if (/^\d+(\.\d+)?$/.test(trimmed)) {
    const asNumber = parseFloat(trimmed);
    if (!isNaN(asNumber) && asNumber >= 0) {
      return Math.round(asNumber * 60 * 60); // Convert hours to seconds
    }
  }

  // 3. Try parse-duration library (handles "1h 30m", "90 minutes", "2 hours", etc.)
  const parsed = parse(trimmed, 's');
  if (parsed !== null && parsed >= 0) {
    return Math.round(parsed);
  }

  return null;
}

function validateNaturalAndEmit(): boolean {
  const parsed = parseNaturalInput(naturalInputValue.value);

  if (parsed === null) {
    inputError.value = true;
    inputErrorMessage.value = 'Invalid time format';
    return false;
  }

  inputError.value = false;
  inputErrorMessage.value = '';

  // Update internal state and format display
  internalSeconds.value = parsed;
  naturalInputValue.value = formatSecondsForDisplay(parsed);

  // Also sync ledger mode state
  rawDigits.value = secondsToRawDigits(parsed);

  emit('seconds-updated', parsed);
  return true;
}

// ============== MODE SWITCHING ==============

function setInputMode(mode: InputMode) {
  // Sync state before switching
  if (inputMode.value === 'ledger' && mode === 'natural') {
    // Going from ledger to natural - sync the value
    const seconds = rawDigitsToSeconds();
    naturalInputValue.value = formatSecondsForDisplay(seconds);
  } else if (inputMode.value === 'natural' && mode === 'ledger') {
    // Going from natural to ledger - parse and sync
    const parsed = parseNaturalInput(naturalInputValue.value);
    if (parsed !== null) {
      rawDigits.value = secondsToRawDigits(parsed);
    }
  }

  inputMode.value = mode;
  inputError.value = false;
  inputErrorMessage.value = '';

  // Focus the input after mode switch
  nextTick(() => {
    inputRef.value?.focus();
  });
}

// ============== EXPOSED API ==============

onMounted(() => {
  if (props.autoFocus && !props.readonly) {
    focus();
  }
});

function focus() {
  if (props.readonly) return;

  nextTick(() => {
    inputRef.value?.focus();
    if (inputMode.value === 'ledger') {
      selectAll();
    }
  });
}

function blur() {
  inputRef.value?.blur();
}

// Get current seconds value synchronously (for validation before blur)
function getCurrentSeconds(): number {
  if (inputMode.value === 'ledger') {
    return rawDigitsToSeconds();
  } else {
    const parsed = parseNaturalInput(naturalInputValue.value);
    return parsed ?? internalSeconds.value;
  }
}

// Force validation and emit (for external callers like modal save)
function forceValidateAndEmit(): boolean {
  if (inputMode.value === 'ledger') {
    return validateLedgerAndEmit();
  } else {
    return validateNaturalAndEmit();
  }
}

defineExpose({
  inputError,
  focus,
  blur,
  focusContainer: focus,
  getCurrentSeconds,
  forceValidateAndEmit,
});
</script>

<style lang="scss">
@import '../../styles/mixins';

.time-input {
  @apply tw-relative tw-text-gray-dark tw-text-sm;

  &__container {
    @apply tw-flex tw-items-center tw-cursor-pointer;
    height: 2rem;

    &--border {
      @apply tw-border tw-border-gray-medium tw-rounded tw-overflow-hidden;
    }

    &--no-border {
      @apply tw-flex tw-items-center tw-rounded tw-overflow-hidden;
    }

    &:focus-within {
      outline: 1px solid var(--q-primary);
      outline-offset: 1px;
    }
  }

  &__field {
    @apply tw-flex-1 tw-px-2 tw-text-xs tw-text-gray-dark tw-bg-transparent;
    @apply tw-outline-none tw-border-0;
    min-width: 0;
    height: 2rem;
    line-height: 2rem;
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.05em;

    &::placeholder {
      @apply tw-text-gray-medium;
    }

    &:focus {
      outline: none !important;
      box-shadow: none !important;
    }

    &:read-only {
      @apply tw-cursor-default;
    }
  }

  &__mode-toggle {
    @apply tw-flex tw-items-center tw-gap-0.5 tw-mr-1 tw-shrink-0;
  }

  &__mode-btn {
    @apply tw-flex tw-items-center tw-justify-center tw-rounded tw-transition-colors;
    @apply tw-text-gray-medium hover:tw-text-gray-dark hover:tw-bg-gray-light;
    width: 20px;
    height: 20px;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;

    &--active {
      @apply tw-text-primary tw-bg-primary tw-bg-opacity-10;
    }

    &:focus {
      outline: 1px solid var(--q-primary);
      outline-offset: 1px;
    }
  }

  &--compact {
    @apply tw-text-xs tw-text-gray-medium-dark;
  }

  &--error {
    @apply tw-border-red;
    @apply tw-text-red;
  }

  &__error-message {
    @apply tw-text-red tw-block;
    font-size: 10px;
  }

  &__label {
    font-size: 10px;
    @apply tw-font-bold tw-uppercase tw-text-gray-medium-dark tw-block;
    &--error {
      @apply tw-text-red;
    }
  }
}
</style>
